import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, X, BookOpen, Mic } from "lucide-react";
import { getMonthData, WRITTEN_TARGET_MIN, ORAL_TARGET_MIN } from "../lib/dailyPlan";
import Card from "./atoms/Card";
import Chip from "./atoms/Chip";

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

export default function MonthlyCalendar({ className = "" }) {
  const today = useMemo(() => new Date(), []);
  const [view, setView] = useState({ year: today.getFullYear(), monthIdx: today.getMonth() });
  const [selected, setSelected] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const { cells, summary } = useMemo(
    () => getMonthData(view.year, view.monthIdx),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [view, refreshTick]
  );

  useEffect(() => {
    const onFocus = () => setRefreshTick((t) => t + 1);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  function prevMonth() {
    setView((v) => v.monthIdx === 0 ? { year: v.year - 1, monthIdx: 11 } : { year: v.year, monthIdx: v.monthIdx - 1 });
  }
  function nextMonth() {
    setView((v) => v.monthIdx === 11 ? { year: v.year + 1, monthIdx: 0 } : { year: v.year, monthIdx: v.monthIdx + 1 });
  }
  function thisMonth() {
    setView({ year: today.getFullYear(), monthIdx: today.getMonth() });
  }

  return (
    <Card className={className} style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <h3 className="font-display" style={{ fontSize: 28, fontWeight: 400, margin: 0, lineHeight: 1.1 }}>
          {summary.monthName} <em style={{ color: "var(--gold-500)" }}>{summary.year}</em>
        </h3>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button type="button" onClick={prevMonth} aria-label="Previous month" style={iconBtn}>
            <ChevronLeft size={16} strokeWidth={1.6} />
          </button>
          <button type="button" onClick={thisMonth} style={{ ...iconBtn, padding: "6px 10px", fontSize: 12, fontWeight: 600, width: "auto" }}>
            Today
          </button>
          <button type="button" onClick={nextMonth} aria-label="Next month" style={iconBtn}>
            <ChevronRight size={16} strokeWidth={1.6} />
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <Chip tone="sage">Perfect · {summary.perfect}</Chip>
        <Chip tone="gold">Partial · {summary.partial}</Chip>
        <Chip tone={summary.missed > 0 ? "berry" : "neutral"}>Missed · {summary.missed}</Chip>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 6 }}>
        {WEEKDAYS.map((d, i) => (
          <div key={i} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: i === 0 || i === 6 ? "var(--rust)" : "var(--fg-3)" }}>
            {d}
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {cells.map((c, i) => {
          if (c.blank) return <div key={`b-${i}`} />;
          const status = c.complete ? "perfect"
            : (c.writtenDone || c.oralDone) ? "partial"
            : c.isFuture ? "future"
            : (c.writtenMinutes === 0 && c.oralMinutes === 0) ? "missed"
            : "started";

          const palette =
            status === "perfect" ? { bg: "var(--sage)",       fg: "var(--paper)",     dot: "var(--paper)" }
          : status === "partial" ? { bg: "var(--gold-200)",   fg: "var(--ink-900)",   dot: "var(--gold-700)" }
          : status === "started" ? { bg: "#f8efd3",            fg: "var(--gold-700)",  dot: "var(--gold-700)" }
          : status === "missed"  ? { bg: "#fdf0f1",            fg: "var(--berry)",     dot: "var(--berry)" }
          :                        { bg: "transparent",        fg: "var(--ink-300)",   dot: "var(--ink-300)" };

          return (
            <button
              key={c.date}
              type="button"
              onClick={() => setSelected(c)}
              disabled={c.isFuture && !c.writtenMinutes && !c.oralMinutes}
              style={{
                aspectRatio: "1",
                borderRadius: "var(--radius-sm)",
                background: palette.bg,
                color: palette.fg,
                border: c.isToday ? "2px solid var(--gold-500)" : "1px solid var(--border-1)",
                padding: 4,
                cursor: c.isFuture ? "default" : "pointer",
                opacity: c.isFuture ? 0.4 : 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "var(--font-body)",
                transition: "all 150ms cubic-bezier(.2,.8,.2,1)",
              }}
            >
              <span className="font-display" style={{ fontSize: 16, lineHeight: 1 }}>{c.day}</span>
              <span style={{ marginTop: 4, width: 4, height: 4, borderRadius: 999, background: status === "future" ? "transparent" : palette.dot }} />
            </button>
          );
        })}
      </div>

      {selected && <DayDetail day={selected} onClose={() => setSelected(null)} />}
    </Card>
  );
}

function DayDetail({ day, onClose }) {
  const date = new Date(day.date + "T00:00:00");
  const status = day.complete ? "Perfect" : (day.writtenDone || day.oralDone) ? "Partial" : day.isFuture ? "Upcoming" : "Missed";
  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(11,17,29,0.5)", backdropFilter: "blur(6px) saturate(.7)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", border: "1px solid var(--border-2)", borderRadius: "var(--radius-xl)", boxShadow: "var(--shadow-modal)", maxWidth: 380, width: "100%", padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <p className="eyebrow">{date.toLocaleDateString([], { weekday: "long" })}</p>
            <h3 className="font-display" style={{ fontSize: 28, fontWeight: 400, margin: "4px 0 0" }}>
              {date.toLocaleDateString([], { month: "long", day: "numeric" })}
            </h3>
            <p style={{ fontSize: 13, color: "var(--fg-3)", margin: "4px 0 0" }}>{status}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={iconBtn}>
            <X size={16} strokeWidth={1.6} />
          </button>
        </div>
        <DetailBar Icon={BookOpen} label="Written" minutes={day.writtenMinutes} target={WRITTEN_TARGET_MIN} done={day.writtenDone} color="var(--gold-500)" />
        <div style={{ height: 12 }} />
        <DetailBar Icon={Mic}      label="Oral"    minutes={day.oralMinutes}    target={ORAL_TARGET_MIN}    done={day.oralDone}    color="var(--rust)" />
      </div>
    </div>
  );
}

function DetailBar({ Icon, label, minutes, target, done, color }) {
  const pct = Math.min(100, Math.round((minutes / target) * 100));
  return (
    <div style={{ background: "var(--paper-deep)", borderRadius: "var(--radius-md)", padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <Icon size={18} strokeWidth={1.5} color={color} />
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--fg-1)" }}>{label}</span>
        <span style={{ marginLeft: "auto", fontSize: 12, color: done ? "var(--sage-dark)" : "var(--fg-3)", fontWeight: 600 }}>
          {Math.floor(minutes)}/{target}m{done ? " ✓" : ""}
        </span>
      </div>
      <div style={{ height: 4, background: "rgba(11,17,29,0.08)", borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, transition: "width 700ms cubic-bezier(.2,.8,.2,1)" }} />
      </div>
    </div>
  );
}

const iconBtn = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  borderRadius: "var(--radius-sm)",
  background: "transparent",
  border: "1px solid var(--border-2)",
  color: "var(--fg-2)",
  cursor: "pointer",
  transition: "background 150ms cubic-bezier(.2,.8,.2,1)",
};
